import React from 'react';
import { Star, CheckCircle, AlertTriangle } from 'lucide-react';

// ─────────────────────────────────────────────────────────────
//  Option label resolver — tolerant of every schema we've seen
//  in field_options across global_custom_fields / custom_fields:
//
//    [{en, zh}]                              ← most preferences fields
//    [{value, label, label_zh}]              ← some admin-created fields
//    [{label, label_en}]                     ← a few legacy fields
//    ["OptionA", "OptionB"]                  ← plain-string options
//
//  Also falls back to a dictionary of common English → Chinese terms
//  (Owns Home / Bachelor's Degree / Married / …) so values submitted
//  before label_zh existed still show in Chinese when viewed in the
//  Chinese UI.
// ─────────────────────────────────────────────────────────────

// Hard-coded fallback map for common English preference-option strings
// seen in historical submissions where no label_zh was stored.
const FALLBACK_ZH = {
  // Housing
  'owns home': '有房', 'no home': '没有房', 'owns abroad': '有房在国外',
  'lives with parents': '与父母同住', 'able to buy': '有购房能力',
  'renting': '租房', 'renter': '租房',
  // Education
  'high school': '高中', 'associate degree': '大专',
  "bachelor's degree": '本科', 'bachelor degree': '本科', 'bachelors': '本科', 'bachelor': '本科',
  "master's degree": '硕士', 'master degree': '硕士', 'masters': '硕士', 'master': '硕士',
  'phd': '博士', 'doctorate': '博士',
  // Marital status
  'never married': '未婚', 'single': '单身', 'divorced': '离异', 'widowed': '丧偶',
  'separated': '分居', 'married': '已婚',
  // Children
  'no children': '无小孩', 'has children': '有小孩', "doesn't want children": '不想要小孩',
  'wants children': '想要小孩',
  // Smoking / drinking
  'never': '从不', 'occasionally': '偶尔', 'socially': '社交场合', 'regularly': '经常',
  'non-smoker': '不吸烟', 'smoker': '吸烟',
  'non-drinker': '不喝酒', 'drinker': '喝酒',
  // Religion
  'buddhist': '佛教', 'christian': '基督教', 'catholic': '天主教',
  'muslim': '伊斯兰教', 'jewish': '犹太教', 'atheist': '无宗教',
  // Job / income / lifestyle
  'yes': '是', 'no': '否', 'maybe': '也许', 'prefer not to say': '不便透露',
  'flexible': '灵活', 'open-minded': '开放', 'conservative': '保守',
  // Parent relationship
  'close': '亲密', 'not close': '不亲密', 'distant': '疏远', 'separate': '分开住',
  'living together': '一起住', 'dependent': '需要照顾',
};

export function optionLabel(val, field, isZh) {
  if (val === null || val === undefined || val === '') return '';
  if (Array.isArray(val) || typeof val === 'object') return String(val); // should already be handled

  const vStr = String(val);

  // Walk field_options (if present) for a matching shape-agnostic entry.
  if (field?.field_options && Array.isArray(field.field_options)) {
    for (const opt of field.field_options) {
      if (typeof opt === 'string') {
        if (opt === vStr) return opt;
        continue;
      }
      if (!opt || typeof opt !== 'object') continue;
      const matches =
        opt.value === vStr ||
        opt.en === vStr ||
        opt.zh === vStr ||
        opt.label === vStr ||
        opt.label_en === vStr ||
        opt.label_zh === vStr;
      if (matches) {
        return isZh
          ? (opt.zh || opt.label_zh || opt.label || opt.label_en || opt.en || vStr)
          : (opt.en || opt.label_en || opt.label || opt.value || vStr);
      }
    }
  }

  // Last-resort dictionary (common English terms → Chinese).
  if (isZh) {
    const zh = FALLBACK_ZH[vStr.toLowerCase()];
    if (zh) return zh;
  }
  return vStr;
}

/**
 * Shared 4-column preference results table used anywhere the
 * 人生合伙人画像 (profile_preferences) questionnaire result is shown.
 *
 * Columns: 维度 / 理想标准 / 可接受范围 / 绝对底线
 *
 * Props:
 *   preferenceResults: [{
 *     field_id, field_label, field_label_zh,
 *     field_type ('select' | 'range' | 'multiselect' | 'text' | ...),
 *     field_options (optional — used to resolve option labels),
 *     value: { ideal, acceptable, bottom_line }  // or a legacy primitive
 *   }, ...]
 *   isZh: boolean
 *   maxHeight: optional CSS max-height for the scroll area (default '70vh')
 */
export default function PreferencesTable({ preferenceResults = [], isZh, maxHeight = '70vh' }) {
  // ── Coerce strings that look like JSON into their parsed form ────
  // Inner tier values (ideal / acceptable / bottom_line) sometimes arrive
  // as JSON strings because the backend only parses the outer wrapper.
  // This recursive normaliser makes the formatter shape-agnostic.
  const normalise = (v) => {
    if (typeof v === 'string') {
      const s = v.trim();
      // Legacy data: a buggy older save path coerced an object via String()
      // into the literal token "[object Object]". Treat as empty so the cell
      // renders an em-dash instead of leaking the bug to users.
      if (s === '[object Object]') return '';
      if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
        try { return JSON.parse(s); } catch (_) { /* fall through */ }
      }
    }
    return v;
  };

  // ── Primitive value formatter (range / array / select / plain) ────
  const formatPrimitiveValue = (raw, field) => {
    const val = normalise(raw);
    if (val === null || val === undefined || val === '') return '';

    // Range: any object carrying start / end keys — auto-detected, no
    // dependency on field.field_type which isn't always set to 'range'.
    if (typeof val === 'object' && !Array.isArray(val) && ('start' in val || 'end' in val)) {
      const startRaw = val.start;
      const endRaw = val.end;
      // Defensive: skip values that are themselves objects (would produce "[object Object]").
      const start = (startRaw && typeof startRaw === 'object') ? '' : (startRaw ?? '').toString().trim();
      const end = (endRaw && typeof endRaw === 'object') ? '' : (endRaw ?? '').toString().trim();
      if (start && end) return `${start} - ${end}`;
      if (start) return `${start}+`;
      if (end) return isZh ? `最多 ${end}` : `Up to ${end}`;
      return '';
    }

    if (Array.isArray(val)) {
      if (val.length === 0) return '';
      return val.map(v => {
        const nv = normalise(v);
        // Recurse for nested objects (range, etc.) — otherwise optionLabel
        // would coerce them to "[object Object]".
        if (nv && typeof nv === 'object') {
          return formatPrimitiveValue(nv, field);
        }
        return optionLabel(nv, field, isZh);
      }).join(isZh ? '、' : ', ');
    }

    if (typeof val === 'object') {
      // Unknown object shape — fall back to a readable key:value list instead
      // of dumping raw JSON. Keeps the UI useful even for new field types.
      const entries = Object.entries(val).filter(([, v]) => v !== '' && v !== null && v !== undefined);
      if (entries.length === 0) return '';
      return entries.map(([k, v]) => `${k}: ${String(normalise(v))}`).join(isZh ? '； ' : '; ');
    }

    // Select option value → label lookup (single value)
    return optionLabel(val, field, isZh);
  };

  // ── Empty-check for a single tier cell ───────────────────────
  const isCellEmpty = (raw) => {
    const v = normalise(raw);
    if (v === null || v === undefined || v === '') return true;
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'object') {
      if (v.start !== undefined || v.end !== undefined) {
        return !(v.start || '').toString().trim() && !(v.end || '').toString().trim();
      }
      return Object.keys(v).length === 0;
    }
    return false;
  };

  const renderCell = (tierVal, field) => {
    if (isCellEmpty(tierVal)) return <span style={{ color: '#C9C3BA' }}>—</span>;
    return formatPrimitiveValue(tierVal, field);
  };

  if (!preferenceResults || preferenceResults.length === 0) {
    return null;
  }

  return (
    <div style={{ padding: '12px 16px 20px', maxHeight, overflow: 'auto' }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse', fontSize: 13,
        fontFamily: "'Jost', 'Noto Sans SC', 'PingFang SC', sans-serif",
        border: '1px solid #E4DFD7',
      }}>
        <thead>
          <tr style={{ background: '#F6F3EE' }}>
            <th style={{
              padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700,
              color: '#3A3835', border: '1px solid #E4DFD7', minWidth: 110,
            }}>{isZh ? '维度' : 'Dimension'}</th>
            <th style={{
              padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700,
              color: '#8A6D3B', border: '1px solid #E4DFD7',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Star size={14} style={{ color: '#8A6D3B' }} fill="#B8975A" />
                {isZh ? '理想标准' : 'Ideal Standard'}
              </span>
            </th>
            <th style={{
              padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700,
              color: '#3F7D5A', border: '1px solid #E4DFD7',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle size={14} style={{ color: '#3F7D5A' }} />
                {isZh ? '可接受范围' : 'Acceptable Range'}
              </span>
            </th>
            <th style={{
              padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700,
              color: '#9E2B25', border: '1px solid #E4DFD7',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={14} style={{ color: '#9E2B25' }} />
                {isZh ? '绝对底线' : 'Absolute Bottom Line'}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {preferenceResults.map((field) => {
            const val = normalise(field.value);
            const isThreeDim = val && typeof val === 'object' && !Array.isArray(val)
              && ('ideal' in val || 'acceptable' in val || 'bottom_line' in val);
            return (
              <tr key={field.field_id}>
                <td style={{
                  padding: '10px 12px', fontSize: 13, fontWeight: 600,
                  color: '#111111', border: '1px solid #E4DFD7', verticalAlign: 'top',
                  background: '#F6F3EE',
                }}>
                  {isZh ? (field.field_label_zh || field.field_label) : field.field_label}
                </td>
                {isThreeDim ? (
                  <>
                    <td style={tdStyle}>{renderCell(val.ideal, field)}</td>
                    <td style={tdStyle}>{renderCell(val.acceptable, field)}</td>
                    <td style={tdStyle}>{renderCell(val.bottom_line, field)}</td>
                  </>
                ) : (
                  // Saved before 三维 (one plain value): shown as 可接受, 理想 / 底线 empty.
                  <>
                    <td style={tdStyle}>{renderCell(null, field)}</td>
                    <td style={tdStyle}>{renderCell(val, field)}</td>
                    <td style={tdStyle}>{renderCell(null, field)}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const tdStyle = {
  padding: '10px 12px', fontSize: 13, color: '#3A3835',
  border: '1px solid #E4DFD7', verticalAlign: 'top', lineHeight: 1.5,
};
